/**
 * Service Tianji Frontend pour le tracking des événements
 *
 * Utilise l'API native Tianji pour le tracking :
 * - Script tag : tianji.track(eventName, data)
 * - Attributs HTML : data-tianji-event="eventName"
 *
 * Gère le tracking côté client pour les KPI :
 * - KPI 1: reservation_started
 * - KPI 8: page_view (usage mobile)
 * - KPI 9: page_view (mini-site)
 * - KPI 10: support_clicked
 */

import type { App } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { apiService } from "@/services/api.service";
import { PUBLIC } from "@/config/api.endpoints";
import axios from "axios";

interface TianjiEventProperties {
  [key: string]: unknown;
}

interface TianjiConfig {
  url?: string;
  websiteId?: string;
  enabled: boolean;
  debug: boolean;
}

// Déclaration globale pour TypeScript
declare global {
  interface Window {
    tianji?: {
      track: (eventName: string, data?: TianjiEventProperties) => void;
    };
  }
}

class TianjiService {
  private config: TianjiConfig;
  private eventQueue: Array<{
    event: string;
    properties: TianjiEventProperties;
  }> = [];
  private isInitialized = false;

  constructor() {
    // Configuration temporaire avec valeurs par défaut
    // Sera remplacée par la config de l'API
    this.config = {
      url: "",
      websiteId: "",
      enabled: false,
      debug: import.meta.env.MODE === "development",
    };

    // Charger la configuration depuis l'API seulement si pas en mode test
    if (import.meta.env.MODE !== "test") {
      this.loadConfigFromAPI();
    }
  }

  /**
   * Charge la configuration Tianji depuis l'API backend
   */
  private async loadConfigFromAPI(): Promise<void> {
    try {
      const apiConfig = await apiService.get<{
        url?: string;
        websiteId?: string;
        enabled: boolean;
      }>(PUBLIC.CONFIG.TIANJI);

      this.config = {
        ...this.config,
        url: apiConfig?.url || "",
        websiteId: apiConfig?.websiteId || "",
        enabled: apiConfig?.enabled ?? false,
      };

      if (this.config.debug) {
        console.warn("Tianji config loaded from API:", this.config);
      }
    } catch (error) {
      const isExpectedDisabledConfig =
        axios.isAxiosError(error) && error.response?.status === 404;
      if (isExpectedDisabledConfig) {
        console.warn(
          "Tianji config endpoint unavailable; falling back to disabled/env config.",
        );
      } else {
        console.warn("Error loading Tianji config from API:", error);
      }
      this.applyEnvConfigFallback();
    }
  }

  private applyEnvConfigFallback(): void {
    this.config = {
      url: import.meta.env.VITE_TIANJI_URL,
      websiteId: import.meta.env.VITE_TIANJI_WEBSITE_ID,
      enabled: import.meta.env.VITE_TIANJI_ENABLED === "true",
      debug: import.meta.env.MODE === "development",
    };
  }

  /**
   * Initialise le service Tianji
   * Attend que le script Tianji soit chargé
   */
  init(): void {
    if (this.isInitialized) return;

    // Attendre que le script Tianji soit chargé
    this.waitForTianjiScript().then(() => {
      this.isInitialized = true;

      // Auto-track des page views
      this.setupPageViewTracking();

      // Traiter la queue d'événements en attente
      this.flushEventQueue();

      if (this.config.debug) {
        // Console log removed for production
      }
    });
  }

  /**
   * Waits for the Tianji script to be injected into the page.
   * Resolves immediately if already present, otherwise polls every 100 ms.
   * Gives up after MAX_WAIT_MS to avoid an infinite loop when Tianji is disabled
   * or the script tag is never injected (e.g. env-vars missing, adblocker, etc.).
   */
  private async waitForTianjiScript(): Promise<void> {
    const MAX_WAIT_MS = 10_000;
    const POLL_INTERVAL_MS = 100;

    return new Promise((resolve) => {
      if (window.tianji) {
        resolve();
        return;
      }

      let elapsed = 0;
      const checkTianji = () => {
        if (window.tianji) {
          resolve();
          return;
        }
        elapsed += POLL_INTERVAL_MS;
        if (elapsed >= MAX_WAIT_MS) {
          resolve();
          return;
        }
        setTimeout(checkTianji, POLL_INTERVAL_MS);
      };

      checkTianji();
    });
  }

  /**
   * Track un événement via l'API native Tianji
   */
  async track(
    eventName: string,
    properties: TianjiEventProperties = {},
  ): Promise<void> {
    if (!this.config.enabled) return;

    const authStore = useAuthStore();

    // Enrichir avec des données contextuelles
    const enrichedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      user_id: authStore.user?.id,
      organization_id: authStore.user?.organization_id,
      device: this.getDeviceType(),
      user_agent: navigator.userAgent,
      page: window.location.pathname,
      source: "mylimni_frontend",
    };

    if (!this.isInitialized || !window.tianji) {
      // Ajouter à la queue si pas encore initialisé
      this.eventQueue.push({
        event: eventName,
        properties: enrichedProperties,
      });
      return;
    }

    if (this.config.debug) {
      // Console log removed for production
    }

    try {
      // Utiliser l'API native Tianji
      window.tianji.track(eventName, enrichedProperties);
    } catch (error) {
      console.error("Failed to send event to Tianji:", error);
    }
  }

  /**
   * Configuration automatique du tracking des page views
   */
  private setupPageViewTracking(): void {
    // Track page view initiale
    this.trackPageView();

    // Track les changements de route (pour SPA)
    window.addEventListener("popstate", () => {
      setTimeout(() => this.trackPageView(), 100);
    });

    // Observer les changements d'URL (pour router programmatique)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      setTimeout(() => tianjiService.trackPageView(), 100);
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      setTimeout(() => tianjiService.trackPageView(), 100);
    };
  }

  /**
   * Vide la queue d'événements en attente
   */
  private async flushEventQueue(): Promise<void> {
    if (!window.tianji) return;

    while (this.eventQueue.length > 0) {
      const { event, properties } = this.eventQueue.shift()!;

      try {
        window.tianji.track(event, properties);

        if (this.config.debug) {
          // Console log removed for production
        }
      } catch (error) {
        console.error("Failed to send queued event to Tianji:", error);
      }
    }
  }

  /**
   * Détecte le type d'appareil
   */
  private getDeviceType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      );
    const isTablet = /ipad|tablet|(android(?!.*mobile))/i.test(userAgent);

    if (isMobile && !isTablet) return "mobile";
    if (isTablet) return "tablet";
    return "desktop";
  }

  // ============================================================================
  // MÉTHODES SPÉCIFIQUES POUR CHAQUE KPI
  // ============================================================================

  /**
   * KPI 1: Track le début d'une réservation
   */
  async trackReservationStarted(
    lakeId: string,
    additionalProps: TianjiEventProperties = {},
  ): Promise<void> {
    await this.track("reservation_started", {
      lake_id: lakeId,
      ...additionalProps,
    });
  }

  /**
   * KPI 8 & 9: Track les vues de page
   */
  async trackPageView(
    additionalProps: TianjiEventProperties = {},
  ): Promise<void> {
    const currentPath = window.location.pathname;
    const isMinSite = currentPath.startsWith("/mini-site/");
    const isMobile = this.getDeviceType() === "mobile";

    await this.track("page_view", {
      page: currentPath,
      is_mobile: isMobile,
      is_mini_site: isMinSite,
      referrer: document.referrer,
      ...additionalProps,
    });
  }

  /**
   * KPI 10: Track les clics sur le support
   */
  async trackSupportClicked(
    supportType: string = "unknown",
    additionalProps: TianjiEventProperties = {},
  ): Promise<void> {
    await this.track("support_clicked", {
      support_type: supportType,
      ...additionalProps,
    });
  }

  /**
   * Track une activation de fonctionnalité
   */
  async trackFeatureActivated(
    featureName: string,
    context: string = "unknown",
  ): Promise<void> {
    await this.track("feature_activated", {
      feature: featureName,
      context,
    });
  }

  // ============================================================================
  // UTILITAIRES POUR DATA-TIANJI-EVENT
  // ============================================================================

  /**
   * Ajoute un attribut data-tianji-event à un élément du DOM
   * Note: Les attributs data-tianji-event sont automatiquement détectés par le script Tianji
   */
  addEventAttribute(element: HTMLElement, eventName: string): void {
    element.setAttribute("data-tianji-event", eventName);

    if (this.config.debug) {
      // Console log removed for production
    }
  }

  /**
   * Retire un attribut data-tianji-event d'un élément
   */
  removeEventAttribute(element: HTMLElement): void {
    element.removeAttribute("data-tianji-event");
  }
}

// Instance globale
export const tianjiService = new TianjiService();

// Plugin Vue pour injection globale
export default {
  install(app: App) {
    app.config.globalProperties.$tianji = tianjiService;
    app.provide("tianji", tianjiService);
  },
};
